import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-add-edit-package',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './add-edit-package.component.html',
  styleUrl: './add-edit-package.component.scss'
})
export class AddEditPackageComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() quarters: Array<{ Value: string }> = [];
  @Input() packagesList:any = [];
  @Input() existingPackages: any[] = [];
  @Input() editingPackage: any | null = null;
  @Input() isEdit: boolean = false;

  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  lookupKey = LookupKey;
  duplicatePackageTypeError = false;
  private destroy$ = new Subject<void>();
  private readonly branchPackageTypes = ['package 1', 'package 2', 'package 4'];
  private readonly channelPackageTypes = ['package 3', 'branding kit', 'package 5'];
  private readonly quarterPackageTypes = ['package 1', 'package 2', 'package 3', 'package 4', 'package 5'];

  ngOnInit(): void {
    this.applyPackageTypeRules(this.getPackageTypeValue());
    this.calculateTotals();

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.duplicatePackageTypeError = false;
      this.applyPackageTypeRules(this.getPackageTypeValue());
      this.calculateTotals();
    });
  }

  onPackageTypeChange(option: any) {
    this.duplicatePackageTypeError = false;
    if (option && option.selectedLov) {
      const { Low, High } = option.selectedLov;
      if (Low !== undefined) {
        this.form.get('unitPrice')?.setValue(Low);
      }
      if (High !== undefined) {
        this.form.get('vatAmount')?.setValue(High);
      }
    }
    this.applyPackageTypeRules(this.getPackageTypeValue(option));
    this.calculateTotals();
  }
  onQuartersTypeChange(option: any) {
    let quartersTypes: any = this.form.get('quartersType')?.value;
    if (quartersTypes && quartersTypes.length > 0) {
      this.form.get('quarters')?.setValue(quartersTypes.length);
    }
  }
  onSave(): void {
    if (this.hasDuplicatePackageType()) {
      this.duplicatePackageTypeError = true;
      return;
    }
    this.duplicatePackageTypeError = false;
    this.save.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private applyPackageTypeRules(packageType: string): void {
    const normalizedType = packageType.toLowerCase();
    const branchesRequired = this.branchPackageTypes.includes(normalizedType);
    const channelsRequired = this.channelPackageTypes.includes(normalizedType);
    const quartersRequired = this.quarterPackageTypes.includes(normalizedType);

    this.setControlState('branches', { required: branchesRequired, editable: branchesRequired });
    this.setControlState('channels', { required: channelsRequired, editable: channelsRequired });
    this.setControlState('quartersType', { required: quartersRequired, editable: true });
  }

  private setControlState(
    controlName: string,
    config: { required: boolean; editable: boolean }
  ): void {
    const control = this.form.get(controlName);
    if (!control) {
      return;
    }

    if (config.editable) {
      control.enable({ emitEvent: false });
      if (config.required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
    } else {
      control.disable({ emitEvent: false });
      control.removeValidators(Validators.required);
      control.setValue('', { emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private getPackageTypeValue(option?: any): string {
    const candidate = option?.selectedLov ?? this.form.get('packageType')?.value;
    if (!candidate) {
      return '';
    }
    // if (typeof candidate === 'string') {
    //   return candidate.trim();
    // }
    let selectedPackage: any = this.packagesList.find((pk: any) => pk.Value == candidate.Value || pk.Name == candidate.Value || pk.Value == candidate || pk.Name == candidate);
    if (selectedPackage) {
      return String(selectedPackage.Name).trim();
    }
    // if (candidate.value) {
    //   return String(candidate.value).trim();
    // }
    return String(selectedPackage.Name).trim(); //String(candidate).trim();
  }

  private hasDuplicatePackageType(): boolean {
    const currentType = this.normalizePackageType(this.form.get('packageType')?.value);
    if (!currentType.name && !currentType.value) {
      return false;
    }

    return (this.existingPackages || []).some(pkg => {
      if (this.editingPackage?.packageId && pkg?.packageId === this.editingPackage.packageId) {
        return false;
      }
      const existingType = this.normalizePackageType(pkg?.packageType);
      return this.isSamePackageType(existingType, currentType);
    });
  }

  private normalizePackageType(candidate: any): { name: string; value: string } {
    if (!candidate) {
      return { name: '', value: '' };
    }

    if (typeof candidate === 'string') {
      const matched = this.packagesList.find(
        (pk: any) => pk.Value == candidate || pk.Name == candidate
      );
      return {
        name: matched?.Name ? String(matched.Name).trim() : String(candidate).trim(),
        value: matched?.Value ? String(matched.Value).trim() : String(candidate).trim()
      };
    }

    const selectedPackage: any = this.packagesList.find(
      (pk: any) =>
        pk.Value == candidate.Value ||
        pk.Name == candidate.Value ||
        pk.Value == candidate ||
        pk.Name == candidate
    );

    const name = selectedPackage?.Name ?? candidate?.Name ?? '';
    const value = selectedPackage?.Value ?? candidate?.Value ?? '';

    return {
      name: name ? String(name).trim() : '',
      value: value ? String(value).trim() : ''
    };
  }

  private isSamePackageType(
    existingType: { name: string; value: string },
    currentType: { name: string; value: string }
  ): boolean {
    const existingName = existingType.name.toLowerCase();
    const existingValue = existingType.value.toLowerCase();
    const currentName = currentType.name.toLowerCase();
    const currentValue = currentType.value.toLowerCase();

    if (!existingName && !existingValue) {
      return false;
    }

    const nameMatches =
      existingName !== '' && (existingName === currentName || existingName === currentValue);
    const valueMatches =
      existingValue !== '' && (existingValue === currentName || existingValue === currentValue);

    return nameMatches || valueMatches;
  }

  private calculateTotals(): void {
    const packageType = this.getPackageTypeValue().toLowerCase();
    const branches = this.toNumber(this.form.get('branches')?.value);
    const channels = this.toNumber(this.form.get('channels')?.value);
    const quarters = this.toNumber(this.form.get('quarters')?.value, 0);
    const unitPrice = this.toNumber(this.form.get('unitPrice')?.value);
    const vatAmount = this.toNumber(this.form.get('vatAmount')?.value);

    let quantity = 0;
    let quarterFactor = 1;

    if (this.branchPackageTypes.includes(packageType)) {
      quantity = branches;
      quarterFactor = quarters;
    } else if (this.channelPackageTypes.includes(packageType)) {
      quantity = channels;
      quarterFactor = packageType === 'branding kit' ? 1 : quarters;
    }

    // const totalAmount = quantity * unitPrice * quarterFactor;
    // const totalVatAmount = quantity * vatAmount * quarterFactor;
    const totalAmount =
      Math.round(quantity * unitPrice * quarterFactor * 100) / 100;

    const totalVatAmount =
      Math.round(quantity * vatAmount * quarterFactor * 100) / 100;

    this.form.get('totalAmount')?.setValue(totalAmount || 0, { emitEvent: false });
    this.form.get('totalVatAmount')?.setValue(totalVatAmount || 0, { emitEvent: false });
  }

  private toNumber(value: any, fallback = 0): number {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
